import {PropsWithChildren, useEffect, useMemo} from "react";
import {Navigate, useLocation} from "react-router-dom";
import {BounceRole} from "@bounce/shared/consumer/declarations";
import {getActiveTokenRole} from "@bounce/shared/contexts";
import {useAuth} from "~/auth/useAuth";
import {useConsumerUser} from "~/contexts";
import {consumerUrls as urls} from "~/routes/consumerUrls";
import {AgentPageLoading} from "./AgentPageLoading";
import {buildConsentOrigin} from "./buildConsentOrigin";

export const AgentConsentGuard = ({children}: PropsWithChildren) => {
  const {credentials, token, isAgent, userId, clearCredentials} = useAuth();
  const {preLoginToken} = useConsumerUser();
  const {pathname, search} = useLocation();

  const credentialsRole = useMemo(() => getActiveTokenRole(credentials?.token), [credentials?.token]);
  const hasConsumerLogin = !!credentials && !isAgent && credentialsRole === BounceRole.USER;
  const shouldClearCredentials = !!credentials && !hasConsumerLogin;
  const isTokenSynced = token === (credentials?.token || preLoginToken || undefined);

  useEffect(() => {
    if (shouldClearCredentials) {
      clearCredentials();
    }
  }, [shouldClearCredentials]);

  if (shouldClearCredentials || !isTokenSynced) {
    return <AgentPageLoading />;
  }

  if (hasConsumerLogin) {
    return <>{children}</>;
  }

  const loginUrl = userId ? `${urls.login.url}?id=${userId}` : urls.login.url;
  return <Navigate to={loginUrl} state={{origin: buildConsentOrigin(pathname, search)}} />;
};
